import * as React from 'react';

import { enumBrokeredOrderStatus } from '@rematter/frontend-lib-api-client';
import { Row } from '@rematter/ui-web';
import { Provider, useAtom, useSetAtom } from 'jotai';

import { brokeredOrderLinkOrdersAtom } from '../../_graphql/mutationBrokeredOrderLinkOrders';
import * as state from '../../_state/brokeredOrderDetail.state';
import { LinkedOrderType } from '../../_state/linkedOrdersTab.state';
import {
  BrokeredOrderDetailRecord,
  OrderRecordForLink,
} from '../../types/brokeredOrder';

import { LinkedOrderDetail } from './LinkedOrderDetail';
import { LinkedOrdersSidebar } from './LinkedOrdersSidebar';
import { LinkOrderDialog } from './LinkOrderDialog';

import { PurchaseOrderWizard } from '~/modules/trade/_components/purchase-orders/_PurchaseOrderWizard';
import { SalesOrderWizard } from '~/modules/trade/_components/salesOrders/_SalesOrderWizard';

const toOrderRecordForLink = (
  order: BrokeredOrderDetailRecord['purchaseOrders'][number],
): OrderRecordForLink => ({
  id: order.id,
  documentNumber: order.documentNumber,
  status: order.status,
  accountName: order.accountName,
  address: order.address,
  lineItems: Array.from({ length: order.lineItemCount }, (_, i) => ({
    id: `${order.id}-${i}`,
  })),
  total: order.total,
});

const withIsolatedProvider =
  <P extends object>(Component: React.FC<P>): React.FC<P> =>
  (props) => (
    <Provider>
      <Component {...props} />
    </Provider>
  );

const IsolatedSalesOrderWizard = withIsolatedProvider(SalesOrderWizard);
const IsolatedPurchaseOrderWizard = withIsolatedProvider(PurchaseOrderWizard);

type Props = {
  order: BrokeredOrderDetailRecord;
  onRefetch?: () => void;
};

export const BrokeredOrderTabLinkedOrders: React.FC<Props> = ({
  order,
  onRefetch,
}) => {
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useAtom(
    state.$isLinkDialogOpen,
  );
  const [linkDialogType, setLinkDialogType] = useAtom(state.$linkDialogType);

  const [isSalesOrderWizardOpen, setIsSalesOrderWizardOpen] =
    React.useState(false);
  const [isPurchaseOrderWizardOpen, setIsPurchaseOrderWizardOpen] =
    React.useState(false);

  const linkOrders = useSetAtom(brokeredOrderLinkOrdersAtom({}));

  const canEdit =
    order.status === enumBrokeredOrderStatus.Draft ||
    order.status === enumBrokeredOrderStatus.Open;

  const purchaseOrders = React.useMemo(
    () => (order.purchaseOrders ?? []).map(toOrderRecordForLink),
    [order.purchaseOrders],
  );

  const salesOrders = React.useMemo(
    () => (order.salesOrders ?? []).map(toOrderRecordForLink),
    [order.salesOrders],
  );

  const handleLinkOrder = React.useCallback(
    (type: LinkedOrderType) => {
      setLinkDialogType(type);
      setIsLinkDialogOpen(true);
    },
    [setLinkDialogType, setIsLinkDialogOpen],
  );

  const handleCreateOrder = React.useCallback(() => {
    setIsLinkDialogOpen(false);
    if (linkDialogType === 'salesOrder') {
      setIsSalesOrderWizardOpen(true);
    } else {
      setIsPurchaseOrderWizardOpen(true);
    }
  }, [linkDialogType, setIsLinkDialogOpen]);

  const handleWizardSuccess = React.useCallback(
    (type: LinkedOrderType) => async (createdOrderId?: string) => {
      if (createdOrderId) {
        await linkOrders({
          id: order.id,
          ...(type === 'purchaseOrder'
            ? { addPurchaseOrderIds: [createdOrderId] }
            : { addSalesOrderIds: [createdOrderId] }),
        });
      }
      onRefetch?.();
    },
    [order.id, linkOrders, onRefetch],
  );

  const existingOrderIds = React.useMemo(
    () => [
      ...(order.purchaseOrders ?? []).map((po) => po.id),
      ...(order.salesOrders ?? []).map((so) => so.id),
    ],
    [order.purchaseOrders, order.salesOrders],
  );

  return (
    <Row className="@BrokeredOrderTabLinkedOrders h-full content-start gap-4 p-4">
      <LinkedOrdersSidebar
        purchaseOrders={purchaseOrders}
        salesOrders={salesOrders}
        canEdit={canEdit}
        onLinkOrder={handleLinkOrder}
      />

      <div className="h-full min-h-0 flex-1 overflow-y-auto rounded-lg border border-neutral/10 bg-white">
        <LinkedOrderDetail onRefetch={onRefetch} />
      </div>

      <LinkOrderDialog
        isOpen={isLinkDialogOpen}
        brokeredOrderId={order.id}
        orderType={linkDialogType}
        existingOrderIds={existingOrderIds}
        onClose={() => setIsLinkDialogOpen(false)}
        onSuccess={() => onRefetch?.()}
        onCreate={handleCreateOrder}
      />

      {isSalesOrderWizardOpen && (
        <IsolatedSalesOrderWizard
          isOpen={isSalesOrderWizardOpen}
          onOpenChange={setIsSalesOrderWizardOpen}
          onSuccess={handleWizardSuccess('salesOrder')}
        />
      )}

      {isPurchaseOrderWizardOpen && (
        <IsolatedPurchaseOrderWizard
          isOpen={isPurchaseOrderWizardOpen}
          onOpenChange={setIsPurchaseOrderWizardOpen}
          onSuccess={handleWizardSuccess('purchaseOrder')}
        />
      )}
    </Row>
  );
};
